// Send fleet rules the screen needs before asking the server: which missions fit the target (the same
// checks as `dispatchFleet` in the server), targets by distance, and the beginner-shield warning.
import type { MapSystem } from '@stars-and-ships/api-types';
import type { SegmentOption } from './components/ui';
import { MISSION_LABELS } from './labels';

export const DISPATCH_MISSIONS = ['attack', 'raid', 'reinforce', 'transfer'] as const;
export type DispatchMission = (typeof DISPATCH_MISSIONS)[number];

export interface Viewer {
  playerId: string;
  alliance: { id: string } | null;
}
type Target = Pick<MapSystem, 'owner' | 'ownerShielded'> | undefined;

export function targetRelation(
  target: Pick<MapSystem, 'owner'> | undefined,
  me: Viewer | undefined
): 'none' | 'own' | 'ally' | 'other' | 'unowned' {
  if (!target) return 'none';
  if (!target.owner) return 'unowned';
  if (target.owner.playerId === me?.playerId) return 'own';
  if (me?.alliance && target.owner.alliance?.id === me.alliance.id) return 'ally';
  return 'other';
}

/** Why `mission` cannot go to `target`, or null when it can. */
export function missionBlocker(
  mission: DispatchMission,
  target: Target,
  me: Viewer | undefined
): string | null {
  const relation = targetRelation(target, me);
  switch (mission) {
    case 'attack':
    case 'raid':
      if (relation === 'own') return 'You cannot attack your own system';
      if (relation === 'ally') return 'You cannot attack your alliance';
      if (relation === 'other' && target?.ownerShielded) {
        return 'This commander is under the beginner shield';
      }
      return null;
    case 'reinforce':
      return relation === 'none' || relation === 'ally'
        ? null
        : 'Only alliance members’ systems can be reinforced';
    case 'transfer':
      return relation === 'none' || relation === 'own' ? null : 'Transfers go to your own systems';
  }
}

export function missionOptions(target: Target, me: Viewer | undefined) {
  return DISPATCH_MISSIONS.map((value): SegmentOption<DispatchMission> => {
    const hint = missionBlocker(value, target, me);
    return hint
      ? { value, label: MISSION_LABELS[value], disabled: true, hint }
      : { value, label: MISSION_LABELS[value], disabled: false };
  });
}

export function defaultMission(target: Target, me: Viewer | undefined): DispatchMission {
  const relation = targetRelation(target, me);
  return relation === 'own'
    ? 'transfer'
    : relation === 'ally'
      ? 'reinforce'
      : relation === 'other'
        ? 'attack'
        : 'raid';
}

/** The player's choice while the target allows it; otherwise the target's default. */
export function chosenMission(
  choice: DispatchMission | null,
  target: Target,
  me: Viewer | undefined
): DispatchMission {
  return choice && !missionBlocker(choice, target, me) ? choice : defaultMission(target, me);
}

/** Map coordinates are light-years (`packages/core/src/geometry.ts`). */
export const distanceLy = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y);

const lyFormat = new Intl.NumberFormat('en', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
export const formatLy = (ly: number) => `${lyFormat.format(ly)} ly`;

export function targetsByDistance(
  systems: readonly MapSystem[],
  origin: MapSystem | undefined,
  search: string
): { system: MapSystem; distanceLy: number }[] {
  const needle = search.trim().toLowerCase();
  return systems
    .filter((s) => s.id !== origin?.id)
    .filter(
      (s) =>
        !needle ||
        s.name.toLowerCase().includes(needle) ||
        // G-60: a renamed system stays findable by its generated name.
        s.generatedName.toLowerCase().includes(needle) ||
        (s.owner?.displayName.toLowerCase().includes(needle) ?? false)
    )
    .map((system) => ({ system, distanceLy: origin ? distanceLy(origin, system) : 0 }))
    .sort((a, b) => a.distanceLy - b.distanceLy || a.system.name.localeCompare(b.system.name));
}

/** Attacking another commander ends the player's own beginner shield for good (GDD 06 §6.1). */
export function shieldWarning(
  mission: DispatchMission,
  target: Target,
  me: (Viewer & { shieldUntil: string | null }) | undefined,
  now: number
): boolean {
  return (
    (mission === 'attack' || mission === 'raid') &&
    targetRelation(target, me) === 'other' &&
    !target?.ownerShielded &&
    me?.shieldUntil != null &&
    Date.parse(me.shieldUntil) > now
  );
}
